/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import styled, { keyframes } from 'styled-components'
import TokenListForm from './TokenListForm'
import MiniSwitch from '../formUtils/MiniSwitch'
import CustomConfirmationModal from '../CustomConfirmationModal'
import FormModal from '../FormModal'
import { useApi } from '../../hooks/useApi'


interface TokenList {
  isActive: boolean
  lastUpdate: string
  name: string
  updatedAt?: string
  url: string
  _id?: string
}

const TokenLists: React.FC = () => {
  const { callApi } = useApi()
  const [addNew, setAddNew] = useState(false)
  const [tokenRegistryData, setTokenLists] = useState<TokenList[] | null>(null)
  const [deleteParams, setDeleteParams] = useState<string | null>(null)
  const [editItem, setEditItem] = useState<TokenList | null>(null)
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [editedProps, setEditedProps] = useState<Partial<TokenList> | null>(null)

  const fetchTokenList = async () => {
    try {
      const response = await callApi<TokenList[]>('/token-registry/lists')
      if (response) setTokenLists(response)
    } catch (error) {
      console.error('Token list error:', error)
    }
  }

  useEffect(() => { fetchTokenList() }, [])

  const handleConfirmDelete = async () => {
    try {
      await callApi(`/token-registry/lists/${deleteParams}`, {}, 'DELETE')
      fetchTokenList()
    } catch (error) {
      console.error('Error deleting token list item:', error)
    }
    setDeleteParams(null)
  }

  const handleEdit = (item: TokenList, index: number) => {
    setEditIndex(index)
    setEditItem(item)
    setEditedProps({ name: item.name, url: item.url, isActive: item.isActive, lastUpdate: item.lastUpdate })
  }

  const handleSaveEdit = async () => {
    try {
      await callApi(`/token-registry/lists/${editItem?._id}`, {
        name: editedProps?.name,
        url: editedProps?.url,
        isActive: editedProps?.isActive,
        lastUpdate: editedProps?.lastUpdate,
      }, 'PUT')
      fetchTokenList()
    } catch (error) {
      console.error('Error editing token list item:', error)
    }
    setEditIndex(null)
  }

  if (!tokenRegistryData) return <LoaderBar />

  return (
    <Wrap>

      <Header>
        <AddBtn onClick={() => setAddNew(!addNew)}>
          {addNew ? 'Hide form' : '+ Add token list'}
        </AddBtn>
      </Header>

      {addNew && (
        <FormModal onConfirm={() => { fetchTokenList(); setAddNew(false) }} onCancel={() => setAddNew(false)}>
          <TokenListForm onSuccess={() => { fetchTokenList(); setAddNew(false) }} />
        </FormModal>
      )}

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>URL</Th>
              <Th>Last update</Th>
              <Th>Active</Th>
              <Th></Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {tokenRegistryData.map((item, index) => {
              const isEditing = editIndex === index
              const ep = editedProps
              return (
                <tr key={index}>
                  <Td>
                    {isEditing ? (
                      <InlineInput type="text" style={{ width: 160 }} value={ep?.name ?? ''} onChange={(e) => setEditedProps({ ...ep!, name: e.target.value })} />
                    ) : <Name>{item.name}</Name>}
                  </Td>
                  <Td>
                    {isEditing ? (
                      <InlineInput type="text" style={{ width: 300 }} value={ep?.url ?? ''} onChange={(e) => setEditedProps({ ...ep!, url: e.target.value })} />
                    ) : <UrlText>{item.url}</UrlText>}
                  </Td>
                  <Td>
                    {isEditing ? (
                      <InlineInput type="text" style={{ width: 160 }} value={ep?.lastUpdate ?? ''} onChange={(e) => setEditedProps({ ...ep!, lastUpdate: e.target.value })} />
                    ) : <Mono>{moment(item.lastUpdate).format('MMM DD, YYYY HH:mm')}</Mono>}
                  </Td>
                  <Td>
                    {isEditing ? (
                      <MiniSwitch ison={ep!.isActive!} onToggle={() => setEditedProps({ ...ep!, isActive: !ep!.isActive })} />
                    ) : <StatusDot active={item.isActive} />}
                  </Td>
                  <Td>
                    {isEditing ? (
                      <BtnGroup>
                        <SaveBtn onClick={handleSaveEdit}>Save</SaveBtn>
                        <GhostBtn onClick={() => setEditIndex(null)}>Cancel</GhostBtn>
                      </BtnGroup>
                    ) : (
                      <EditBtn onClick={() => handleEdit(item, index)}>Edit</EditBtn>
                    )}
                  </Td>
                  <Td>
                    <DeleteBtn onClick={() => setDeleteParams(item._id ?? null)}>Delete</DeleteBtn>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </TableWrap>

      {deleteParams !== null && (
        <CustomConfirmationModal
          message="Are you sure you want to delete this token list?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteParams(null)}
        />
      )}
    </Wrap>
  )
}

export default TokenLists

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 12px;`
const Header = styled.div`display: flex; justify-content: flex-end;`

const shimmer = keyframes`0% { transform: translateX(-100%); } 100% { transform: translateX(100%); }`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content: ''; display: block; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(111,92,230,0.5), transparent); animation: ${shimmer} 1.4s ease infinite; }
`

const TableWrap = styled.div`overflow-x: auto; border: 1px solid rgba(255,255,255,0.07); border-radius: 8px;`
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`
const Th = styled.th`padding: 10px 12px; text-align: left; color: #a1a1a1; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; border-bottom: 1px solid rgba(255,255,255,0.06);`
const Td = styled.td`padding: 8px 10px; color: #a0a0a0; font-size: 13px; vertical-align: middle; border-bottom: 1px solid rgba(255,255,255,0.04); tr:last-child & { border-bottom: none; }`
const Name = styled.span`font-size: 13px; color: #ededed; font-weight: 500;`
const Mono = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 11px; color: #777;`
const UrlText = styled.span`font-family: 'SF Mono','Fira Code',monospace; font-size: 10px; color: #777; word-break: break-all;`
const StatusDot = styled.span<{ active: boolean }>`display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: ${({ active }) => active ? '#33C2B0' : '#ff4444'};`

const InlineInput = styled.input`
  background: #0d0d0d; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px;
  color: #ededed; font-size: 12px; padding: 0 6px; height: 26px; outline: none;
  &:focus { border-color: rgba(111,92,230,0.5); }
`

const BtnGroup = styled.div`display: flex; gap: 4px;`
const ActionBtn = styled.button`height: 26px; padding: 0 9px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 4px; color: #a0a0a0; font-size: 11px; cursor: pointer; transition: border-color 0.15s, color 0.15s;`
const EditBtn = styled(ActionBtn)`&:hover { border-color: rgba(111,92,230,0.4); color: #a89aff; }`
const SaveBtn = styled(ActionBtn)`&:hover { border-color: rgba(51,194,176,0.4); color: #33C2B0; }`
const GhostBtn = styled(ActionBtn)``
const DeleteBtn = styled(ActionBtn)`&:hover { border-color: rgba(255,68,68,0.4); color: #ff4444; }`

const AddBtn = styled.button`
  height: 28px; padding: 0 12px; background: rgba(111,92,230,0.1); border: 1px solid rgba(111,92,230,0.3);
  border-radius: 5px; color: #a89aff; font-size: 12px; font-weight: 500; cursor: pointer;
  transition: background 0.15s; &:hover { background: rgba(111,92,230,0.2); border-color: rgba(111,92,230,0.5); }
`
